import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useWebsiteAppContext } from '../../hooks/useWebsiteAppContext';
import { formatCurrency } from '../../utils/formatters';
import ProductCard from '../../../miniapp/components/ProductCard';
import { getContentBook } from '../../content';
import { filterCollection, parseCollection } from '../../content/collections';
import { ContentBody } from '../../components/ContentView';
import ContentLink from '../../components/ContentLink';
import {
  getLocalizedEntityField,
  getNormalizedLanguageCode,
} from '../../../miniapp/i18n/localization';
import { LoadingState } from '../../../miniapp/ui-kit/Spinner';
import { manufacturerPath, productPath } from '../../../utils/websiteSsr/paths';
import { WEBSITE_PRODUCT_CARD_SIZES } from '../../../utils/responsiveImages';

export default function CatalogScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    catalogLoading,
    ensureCatalogLoaded,
    categories,
    products,
    addToCart,
    removeFromCart,
    cartItems,
  } = useWebsiteAppContext();
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);

  const [params] = useSearchParams();
  const collection = parseCollection(params.get('collection'));
  const book = getContentBook(i18n.resolvedLanguage ?? i18n.language);
  useEffect(() => { void ensureCatalogLoaded(); },[ensureCatalogLoaded]);

  const languageCode = getNormalizedLanguageCode(i18n.resolvedLanguage ?? i18n.language);

  const filteredProducts = useMemo(() => {
    const available = filterCollection(products,categories,collection);
    if (selectedCategoryId === null) return available;
    return available.filter((product) => product.categoryId === selectedCategoryId);
  }, [products, categories, collection, selectedCategoryId]);

  const categoriesWithProducts = useMemo(() => {
    const categoryIdsWithProducts = new Set(products.map((product) => product.categoryId));
    return categories.filter((category) => categoryIdsWithProducts.has(category.id));
  }, [categories, products]);

  const getProductQuantity = (productId: number, weight: number) => {
    return cartItems
      .filter((item) => item.product.id === productId && item.weight === weight)
      .reduce((total, item) => total + item.quantity, 0);
  };

  if (catalogLoading) {
    return (
      <LoadingState indicator="pulse" label={t('common.loading')} className="min-h-[400px] py-0" />
    );
  }

  return (
    <div className="flex flex-col gap-10">
<header><h1 className="m-0 text-4xl font-bold">{book.hero.title}</h1><p className="max-w-[65ch] text-lg leading-8 text-text-secondary">{book.ui.catalogIntro}</p></header>
      <section><h2 className="text-2xl font-bold">{book.sections.teaTypes.title}</h2><ContentBody section={book.sections.teaTypes} /></section>
      {collection && <div className="flex flex-wrap items-center gap-4"><h2>{book.collections[collection]}</h2><ContentLink href="/catalog">{book.ui.browseAll}</ContentLink></div>}

      {/* Categories Carousel */}
      <section>
        <div className="mb-6 flex items-center justify-between">
          <h2 className="m-0 text-2xl font-bold tracking-tight text-text">
            {t('catalog.categoriesTitle')}
          </h2>
        </div>
        <div className="flex overflow-x-auto pt-4 pb-4 gap-6 -mx-4 px-4 sm:mx-0 sm:px-0">
          {/* "All" Category */}
          <button
            type="button"
            onClick={() => setSelectedCategoryId(null)}
            className={`flex min-w-[80px] flex-col items-center gap-3 border-none bg-transparent p-0 cursor-pointer transition-all ${
              selectedCategoryId === null ? 'scale-105' : 'opacity-60 hover:opacity-100'
            }`}
          >
            <div className={`flex h-16 w-16 items-center justify-center rounded-circle transition-all ${
              selectedCategoryId === null 
                ? 'bg-accent-gradient text-on-accent shadow-lg shadow-accent/20' 
                : 'bg-primary border border-border'
            }`}>
              <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="currentColor">
                <path d="M0 0h24v24H0V0z" fill="none"/><path d="M4 11h5V5H4v6zm0 7h5v-6H4v6zm6 0h5v-6h-5v6zm6 0h5v-6h-5v6zm-6-7h5V5h-5v6zm6-6v6h5V5h-5z"/>
              </svg>
            </div>
            <span className={`text-sm font-semibold ${selectedCategoryId === null ? 'text-accent' : 'text-text'}`}>
              {t('catalog.allTea')}
            </span>
          </button>

          {categoriesWithProducts.map((category) => {
            const categoryName = getLocalizedEntityField(category, 'name', languageCode);
            const imageUrl = category.image?.url ?? category.imageUrl;
            const isSelected = selectedCategoryId === category.id;

            return (
              <button
                key={category.id}
                type="button"
                onClick={() => setSelectedCategoryId(category.id)}
                className={`flex min-w-[80px] flex-col items-center gap-3 border-none bg-transparent p-0 cursor-pointer transition-all ${
                  isSelected ? 'scale-105' : 'opacity-60 hover:opacity-100'
                }`}
              >
                <div className={`relative h-16 w-16 overflow-hidden rounded-circle transition-all ${
                  isSelected 
                    ? 'ring-2 ring-accent ring-offset-2 shadow-lg' 
                    : 'bg-primary border border-border'
                }`}>
                  {imageUrl ? (
                    <img src={imageUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-secondary text-text-secondary text-xs font-bold">
                      {categoryName.substring(0, 1).toUpperCase()}
                    </div>
                  )}
                </div>
                <span className={`text-sm font-semibold whitespace-nowrap ${isSelected ? 'text-accent' : 'text-text'}`}>
                  {categoryName}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Products Section */}
      <section>
        <div className="mb-8 flex items-end justify-between">
          <div>
            <h2 className="m-0 text-2xl font-bold tracking-tight text-text">
              {t('catalog.productsTitle')}
            </h2>
            <p className="m-0 mt-1 text-sm text-text-secondary">
              {selectedCategoryId === null 
                ? t('catalog.allProductsLabel')
                : t('catalog.categoryProductsLabel', { 
                    name: getLocalizedEntityField(categories.find(c => c.id === selectedCategoryId)!, 'name', languageCode)
                  })
              }
            </p>
          </div>
          <span className="text-sm font-medium text-text-secondary opacity-60">
            {t('common.items', { count: filteredProducts.length })}
          </span>
        </div>

        <div className="grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredProducts.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              href={productPath(product)}
              manufacturerHref={product.manufacturer ? manufacturerPath(product.manufacturer) : undefined}
              getCartQuantity={(weight) => getProductQuantity(product.id, weight)}
              onOpen={() => navigate(`/product/${product.id}`)}
              onAddToCart={(weight) => addToCart(product.id, weight)}
              onRemoveFromCart={(weight) => removeFromCart(product.id, weight)}
              onOpenManufacturer={(id) => navigate(`/manufacturer/${id}`)}
              formatCurrency={formatCurrency}
              imageSizes={WEBSITE_PRODUCT_CARD_SIZES}
            />
          ))}
        </div>

        {filteredProducts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="mb-4 rounded-circle bg-secondary p-4 text-text-secondary opacity-20">
              <svg xmlns="http://www.w3.org/2000/svg" height="48" viewBox="0 0 24 24" width="48" fill="currentColor">
                <path d="M0 0h24v24H0V0z" fill="none"/><path d="M11 15h2v2h-2zm0-8h2v6h-2zm1-9C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
              </svg>
            </div>
            <h3 className="m-0 text-lg font-semibold text-text">{t('catalog.noProducts')}</h3>
            <p className="m-0 mt-1 text-text-secondary">{t('catalog.noProductsDescription')}</p>
          </div>
        ) : null}
      </section>
    </div>
  );
}
